import { useSelector } from 'react-redux';
import useTestsSelf from '../../hooks/useTestsSelf';
import useTestsOthers from '../../hooks/useTestsOthers';
import TestRow from '../../components/TestRow';
import DateDivider from '../../components/DateDivider';
import { utcToLocalDateString, isDifferentDay, getTestCD } from '../../data/functions';

const TAG = '[Tests]';

function Tests({ userId = null, calendarData = null }) {
  const profileViewing = useSelector((s) => s.ui.profileViewing);
  const uploadsInProgress = useSelector((s) => s.user.uploadsInProgress);
  const authUserId = useSelector((s) => s.user.authUserId);

  // Use the userId prop if provided, otherwise fall back to profileViewing
  const targetUserId = userId || profileViewing?.id;

  // Determine if viewing own tests: either no targetUserId, or targetUserId equals authUserId
  const isOwnTests = !targetUserId || targetUserId === authUserId;

  // Use appropriate hook based on whether viewing own or other users' tests
  const { tests: selfTests } = useTestsSelf();
  const { tests: otherTests } = useTestsOthers(targetUserId);
  const rawTests = isOwnTests ? selfTests : otherTests;

  // Enrich tests with cycle day from calendar data
  const tests = rawTests.map(test => ({
    ...test,
    cycleDay: getTestCD(test, calendarData)
  }));

  // No tests yet message
  if (tests.length === 0) {
    return (
      <div className="flex h-full items-center justify-center p-12 text-center text-sm text-muted-foreground">
        No tests yet. Tap "Add test result" to start.
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto pt-5 pb-20 scrollbar-hide">
      <ul>
        {/* Combine uploads in progress (only for own tests) with real tests, sorted by time */}
        {[...(isOwnTests ? uploadsInProgress : []), ...tests].map((item, idx) => {
          const isUpload = !item.id || item.status === 'uploading';
          const currentDate = utcToLocalDateString(item.startTime || item.taken_at);

          // Check if this is a new day compared to previous item
          const allItems = [...(isOwnTests ? uploadsInProgress : []), ...tests];
          const prevTimestamp = idx > 0 ? (allItems[idx - 1].startTime || allItems[idx - 1].taken_at) : null;
          const isNewDay = isDifferentDay(item.startTime || item.taken_at, prevTimestamp);

          return (
            <div key={item.id || item.uploadId}>
              {/* Date divider - shows full date when a new day starts */}
              {isNewDay && <DateDivider date={currentDate} />}
              {/* Test row - shows time on top when same day, date on top when different day */}
              <TestRow test={item} allTests={tests} uploading={isUpload} />
            </div>
          );
        })}
      </ul>
    </div>
  );
}

export default Tests;
